import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '@components';

interface RoutingRule {
  topic: string;
  email: string;
  emailError?: string;
}

interface RoutingRuleBuilderProps {
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
}

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Konvertiert String-Format zu Array von Regeln
 * Erwartet Format: "- Topic1 → email1@...\n- Topic2 → email2@..."
 */
const parseRulesFromString = (str: string): RoutingRule[] => {
  if (!str.trim()) return [];

  return str
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && line.includes('→'))
    .map(line => {
      const [topic, email] = line
        .replace(/^-\s*/, '')
        .split('→')
        .map(s => s.trim());
      return { topic, email };
    });
};

/**
 * Konvertiert Array von Regeln zu String-Format
 * Ergebnis: "- Topic1 → email1@...\n- Topic2 → email2@..."
 */
const rulesToString = (rules: RoutingRule[]): string => {
  return rules
    .filter(r => r.topic && r.email && !r.emailError)
    .map(r => `- ${r.topic} → ${r.email}`)
    .join('\n');
};

export const RoutingRuleBuilder: React.FC<RoutingRuleBuilderProps> = ({
  value,
  onChange,
  helperText,
}) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const MAX_RULES = 20;

  // Parse initial value from string
  useEffect(() => {
    setRules(parseRulesFromString(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      return;
    }
    const newRules = [...rules, { topic: '', email: '', emailError: undefined }];
    setRules(newRules);
  };

  const handleRuleChange = (index: number, field: 'topic' | 'email', val: string) => {
    const newRules = [...rules];
    newRules[index][field] = val;

    // Validiere Email wenn Feld geändert wird
    if (field === 'email') {
      if (val && !isValidEmail(val)) {
        newRules[index].emailError = t('validation.invalidEmail');
      } else {
        newRules[index].emailError = undefined;
      }
    }

    setRules(newRules);
    onChange(rulesToString(newRules));
  };

  const handleRemoveRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    setRules(newRules);
    onChange(rulesToString(newRules));
  };

  return (
    <div className="space-y-3">
      {rules.map((rule, index) => (
        <div key={index} className="flex gap-2 items-start">
          <div className="flex-1">
            <Input
              placeholder={t('agents.routingRuleTopicPlaceholder')}
              value={rule.topic}
              onChange={e => handleRuleChange(index, 'topic', e.target.value)}
              size="sm"
            />
          </div>

          <div className="pt-2 text-gray-400 dark:text-gray-500 font-mono text-lg">→</div>

          <div className="flex-1">
            <Input
              placeholder={t('agents.routingRuleEmailPlaceholder')}
              type="email"
              value={rule.email}
              onChange={e => handleRuleChange(index, 'email', e.target.value)}
              size="sm"
              state={rule.emailError ? 'error' : 'default'}
              errorText={rule.emailError}
            />
          </div>

          <button
            onClick={() => handleRemoveRule(index)}
            className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition mt-1"
            title={t('common.remove')}
          >
            <span className="material-icons text-lg">close</span>
          </button>
        </div>
      ))}

      <Button
        onClick={handleAddRule}
        variant="outline"
        size="sm"
        className="w-full"
        disabled={rules.length >= MAX_RULES}
      >
        <span className="material-icons mr-2 text-base">add</span>
        {t('agents.addRoutingRule')}
        {rules.length > 0 && ` (${rules.length}/${MAX_RULES})`}
      </Button>

      {helperText && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{helperText}</p>}
    </div>
  );
};
